import { io } from "socket.io-client";

/**
 * Socket.io Service for real-time messaging
 * Handles connection, message sending, and event listening
 */
class SocketService {
  constructor() {
    this.socket = null;
    this.connected = false;
    this.token = null;
    this.userId = null;
    this.serverUrl = process.env.REACT_APP_API || "http://localhost:5000";
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectInterval = 3000; // 3 seconds
    this.activeConversations = new Set();
    this.isConnecting = false; // Flag to prevent multiple connection attempts
    this.connectionId = null; // Unique ID for each connection
  }

  /**
   * Connect to the Socket.io server
   * @param {string} token - Authentication token
   * @param {string} userId - User ID to connect with
   */
  connect(token, userId) {
    // If already connected or in the process of connecting, don't try to connect again
    if (this.connected || this.isConnecting) {
      console.log("Socket.io already connected or connecting");
      return;
    }

    // Set connecting flag to prevent multiple connection attempts
    this.isConnecting = true;

    this.token = token;

    // Use provided userId or get from local storage
    this.userId = userId;
    if (!this.userId) {
      const profile = JSON.parse(localStorage.getItem("profile"));
      this.userId = profile?.result?._id;
    }

    if (!this.userId) {
      console.error("Cannot connect to Socket.io: User ID not found");
      this.isConnecting = false;
      return;
    }

    // Generate a unique connection ID to prevent duplicate connections
    this.connectionId = `${this.userId}-${Date.now()}`;
    console.log(
      `Creating new Socket.io connection with ID: ${this.connectionId}`
    );

    // Initialize Socket.io connection with auth token
    try {
      console.log("Connecting to Socket.io server:", this.serverUrl);

      this.socket = io(this.serverUrl, {
        auth: {
          token: this.token,
        },
        transports: ["websocket", "polling"],
        reconnection: true,
        reconnectionAttempts: this.maxReconnectAttempts,
        reconnectionDelay: this.reconnectInterval,
        // Prevent multiple connections by using a unique query parameter
        query: {
          userId: this.userId,
          connectionId: this.connectionId,
        },
      });

      // Set up connection event handlers
      this.socket.on("connect", this._handleConnect.bind(this));
      this.socket.on("disconnect", this._handleDisconnect.bind(this));
      this.socket.on("connect_error", this._handleConnectError.bind(this));
      this.socket.on("error", this._handleError.bind(this));

      // Set up message event handlers
      this.socket.on("new_message", this._handleNewMessage.bind(this));
      this.socket.on("typing", this._handleTypingIndicator.bind(this));
      this.socket.on("user_status", this._handleUserStatus.bind(this));
      this.socket.on("read_receipt", this._handleReadReceipt.bind(this));
      this.socket.on(
        "message_delivered",
        this._handleMessageDelivered.bind(this)
      );
      this.socket.on("message_read", this._handleMessageRead.bind(this));
      this.socket.on("message_updated", this._handleMessageUpdated.bind(this));
      this.socket.on("message_deleted", this._handleMessageDeleted.bind(this));

      // Rejoin active conversations after reconnect
      this.socket.on("reconnect", () => {
        console.log("Socket.io reconnected");
        this.activeConversations.forEach((conversationId) => {
          this.joinConversation(conversationId);
        });
      });
    } catch (error) {
      console.error("Error creating Socket.io connection:", error);
      this.isConnecting = false;
    }
  }

  /**
   * Disconnect from the Socket.io server
   */
  disconnect() {
    if (this.socket) {
      console.log(`Disconnecting Socket.io connection: ${this.connectionId}`);
      this.socket.disconnect();
      this.socket = null;
      this.connected = false;
      this.isConnecting = false;
      this.activeConversations.clear();
      this.connectionId = null;
    }
  }

  /**
   * Check if the Socket.io is connected
   * @returns {boolean} - Connection status
   */
  isConnected() {
    return this.connected && this.socket?.connected;
  }

  /**
   * Join a conversation to receive updates
   * @param {string} conversationId - ID of the conversation to join
   */
  joinConversation(conversationId) {
    if (!this.isConnected() || !conversationId) return;

    // Only join if not already in this conversation
    if (!this.activeConversations.has(conversationId)) {
      this.socket.emit("join_conversation", { conversationId });
      this.activeConversations.add(conversationId);
      console.log(`Joined conversation: ${conversationId}`);
    }
  }

  /**
   * Leave a conversation to stop receiving updates
   * @param {string} conversationId - ID of the conversation to leave
   */
  leaveConversation(conversationId) {
    if (!this.isConnected() || !conversationId) return;

    if (this.activeConversations.has(conversationId)) {
      this.socket.emit("leave_conversation", { conversationId });
      this.activeConversations.delete(conversationId);
      console.log(`Left conversation: ${conversationId}`);
    }
  }

  /**
   * Send a message via Socket.io
   * @param {Object} messageData - Message data to send
   */
  sendMessage(messageData) {
    if (!this.isConnected()) {
      console.error("Cannot send message: Socket.io not connected");
      return;
    }

    this.socket.emit("send_message", {
      ...messageData,
      userId: this.userId,
    });
  }

  /**
   * Send typing indicator via Socket.io
   * @param {Object} data - Typing indicator data
   */
  sendTypingIndicator(data) {
    if (!this.isConnected()) return;

    this.socket.emit("typing", {
      ...data,
      userId: this.userId,
    });
  }

  /**
   * Send read receipt via Socket.io
   * @param {Object} data - Read receipt data
   */
  sendReadReceipt(data) {
    if (!this.isConnected()) return;

    this.socket.emit("read_receipt", {
      ...data,
      userId: this.userId,
      timestamp: new Date().toISOString(),
    });
  }

  // Event handlers for connection events
  _handleConnect() {
    this.connected = true;
    this.isConnecting = false;
    this.reconnectAttempts = 0;
    console.log(`Socket.io connected with ID: ${this.connectionId}`);

    // Authenticate with the server
    this.socket.emit("authenticate", {
      userId: this.userId,
      token: this.token,
    });

    // Notify connect callback if exists
    if (this.onConnectCallback) {
      this.onConnectCallback();
    }
  }

  _handleDisconnect(reason) {
    this.connected = false;
    console.log(`Socket.io disconnected: ${reason}`);

    // Notify disconnect callback if exists
    if (this.onDisconnectCallback) {
      this.onDisconnectCallback(reason);
    }
  }

  _handleConnectError(error) {
    this.connected = false;
    this.isConnecting = false;
    console.error("Socket.io connection error:", error);

    // Implement custom reconnect logic if needed
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      console.log(
        `Reconnecting... Attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts}`
      );

      setTimeout(() => {
        if (this.socket) {
          this.socket.connect();
        }
      }, this.reconnectInterval);
    }
  }

  _handleError(error) {
    console.error("Socket.io error:", error);
  }

  // Event handlers for message events
  _handleNewMessage(message) {
    if (this.onNewMessageCallback) {
      this.onNewMessageCallback(message);
    }
  }

  _handleTypingIndicator(data) {
    if (this.onTypingIndicatorCallback) {
      this.onTypingIndicatorCallback(data);
    }
  }

  _handleUserStatus(data) {
    if (this.onUserStatusChangeCallback) {
      this.onUserStatusChangeCallback(data);
    }
  }

  _handleReadReceipt(data) {
    if (this.onReadReceiptCallback) {
      this.onReadReceiptCallback(data);
    }
  }

  _handleMessageDelivered(data) {
    if (this.onMessageDeliveredCallback) {
      this.onMessageDeliveredCallback(data);
    }
  }

  _handleMessageRead(data) {
    if (this.onMessageReadCallback) {
      this.onMessageReadCallback(data);
    }
  }

  _handleMessageUpdated(data) {
    if (this.onMessageUpdatedCallback) {
      this.onMessageUpdatedCallback(data);
    }
  }

  _handleMessageDeleted(data) {
    if (this.onMessageDeletedCallback) {
      this.onMessageDeletedCallback(data);
    }
  }

  // Event listener registration methods
  onNewMessage(callback) {
    this.onNewMessageCallback = callback;
  }

  onTypingIndicator(callback) {
    this.onTypingIndicatorCallback = callback;
  }

  onUserStatusChange(callback) {
    this.onUserStatusChangeCallback = callback;
  }

  onReadReceipt(callback) {
    this.onReadReceiptCallback = callback;
  }

  onMessageDelivered(callback) {
    this.onMessageDeliveredCallback = callback;
  }

  onMessageRead(callback) {
    this.onMessageReadCallback = callback;
  }

  onMessageUpdated(callback) {
    this.onMessageUpdatedCallback = callback;
  }

  onMessageDeleted(callback) {
    this.onMessageDeletedCallback = callback;
  }

  onConnect(callback) {
    this.onConnectCallback = callback;
  }

  onDisconnect(callback) {
    this.onDisconnectCallback = callback;
  }
}

// Create singleton instance
const socketService = new SocketService();
export default socketService;
